import React, { useState, useEffect, useCallback } from 'react';
import './ImageCarousel.scss';

interface ImageCarouselProps {
    images: string[];
    autoPlayInterval?: number;
    showArrows?: boolean;
    showDots?: boolean;
    maxDots?: number;
    className?: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
    images,
    autoPlayInterval = 5000,
    showArrows = true,
    showDots = true,
    maxDots = 5,
    className = '',
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    const goToNext = useCallback(() => {
        setCurrentIndex((prevIndex) =>
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    }, [images.length]);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    }, [images.length]);

    const goToSlide = useCallback((index: number) => {
        setCurrentIndex(index);
    }, []);

    const getVisibleDots = useCallback(() => {
        if (images.length <= maxDots) {
            return images.map((_, index) => index);
        }

        const dots = [];
        const halfMax = Math.floor(maxDots / 2);

        if (currentIndex <= halfMax) {
            for (let i = 0; i < maxDots; i++) {
                dots.push(i);
            }
        } else if (currentIndex >= images.length - halfMax - 1) {
            for (let i = images.length - maxDots; i < images.length; i++) {
                dots.push(i);
            }
        } else {
            for (
                let i = currentIndex - halfMax;
                i < currentIndex + halfMax + 1;
                i++
            ) {
                dots.push(i);
            }
        }

        return dots;
    }, [images.length, maxDots, currentIndex]);

    const handleMouseEnter = useCallback(() => {
        setIsAutoPlaying(false);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsAutoPlaying(true);
    }, []);

    useEffect(() => {
        if (!isAutoPlaying || images.length <= 1) return;

        const interval = setInterval(goToNext, autoPlayInterval);
        return () => clearInterval(interval);
    }, [isAutoPlaying, autoPlayInterval, goToNext, images.length]);

    if (!images || images.length === 0) {
        return (
            <div className={`image-carousel ${className}`}>
                <div className="carousel-placeholder">
                    <span>No images available</span>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`image-carousel ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <div className="carousel-container">
                <div className="carousel-inner">
                    <div className="carousel-item active">
                        <img
                            src={images[currentIndex]}
                            alt={`Slide ${currentIndex + 1}`}
                            className="carousel-image"
                        />
                    </div>
                </div>
            </div>
            {showArrows && images.length > 1 && (
                <>
                    <button
                        type="button"
                        className="carousel-control carousel-control-prev"
                        onClick={goToPrevious}
                        aria-label="Previous image">
                        <span className="carousel-control-icon">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none">
                                <path
                                    d="M10 12L6 8L10 4"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </button>
                    <button
                        type="button"
                        className="carousel-control carousel-control-next"
                        onClick={goToNext}
                        aria-label="Next image">
                        <span className="carousel-control-icon">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none">
                                <path
                                    d="M6 4L10 8L6 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </button>
                </>
            )}

            {showDots && images.length > 1 && (
                <div className="carousel-indicators">
                    {getVisibleDots().map((index) => (
                        <button
                            key={index}
                            type="button"
                            className={`carousel-indicator ${
                                index === currentIndex ? 'active' : ''
                            }`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImageCarousel;
